<template>
  <nav
    class="fixed top-0 z-50 bg-white shadow-md transition-all duration-300 w-full"
    :class="{
      'transform -translate-y-full': !isNavbarVisible,
      'transform translate-y-0': isNavbarVisible,
    }"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div class="container mx-auto px-5 lg:px-6 max-w-[1380px] py-4 flex justify-between items-center">
      <!-- Logo -->
      <a href="/" class="flex items-center space-x-2">
        <img
          src="@/assets/images/logopesona.webp"
          alt="Logo PESONA"
          class="h-10 w-auto object-contain"
        />
      </a>

      <!-- Desktop Menu -->
      <ul class="hidden md:flex space-x-8 text-primary font-medium">
        <li>
          <a
            href="#beranda"
            class="hover:text-accent transition-colors duration-200"
            >Beranda</a
          >
        </li>
        <li>
          <a
            href="#teknopedia"
            class="hover:text-accent transition-colors duration-200"
            >Teknopedia</a
          >
        </li>
        <li>
          <a
            href="#kegiatan"
            class="hover:text-accent transition-colors duration-200"
            >Kegiatan</a
          >
        </li>
        <li>
          <a
            href="#tugas"
            class="hover:text-accent transition-colors duration-200"
            >Tugas</a
          >
        </li>
        <li>
          <a
            href="#faq"
            class="hover:text-accent transition-colors duration-200"
            >FAQ</a
          >
        </li>
      </ul>

      <!-- Mobile Menu Button -->
      <button
        @click="toggleMobileMenu"
        class="md:hidden relative z-50 p-2 rounded-lg text-primary focus:outline-none hover:bg-gray-100 transition-all duration-200"
        :class="{ 'text-accent bg-gray-100': isMobileMenuOpen }"
        aria-label="Toggle mobile menu"
        :aria-expanded="isMobileMenuOpen"
      >
        <!-- Simple hamburger/close icon -->
        <div class="w-6 h-6 flex items-center justify-center text-2xl">
          {{ isMobileMenuOpen ? "✕" : "☰" }}
        </div>
      </button>
    </div>

    <!-- Mobile Menu Dropdown -->
    <div
      v-if="isMobileMenuOpen"
      class="md:hidden bg-white shadow-xl border-t border-gray-100 absolute top-full left-0 right-0 z-40 w-full"
    >
      <div class="container mx-auto px-5 lg:px-6 max-w-[1380px]">
        <!-- Mobile Menu Items -->
        <ul class="py-4 space-y-1">
        <li>
          <a
            href="#beranda"
            @click="closeMobileMenu"
            class="block px-6 py-3 text-primary font-medium hover:bg-gray-50 hover:text-accent transition-all duration-200 border-l-4 border-transparent hover:border-accent"
          >
            Beranda
          </a>
        </li>
        <li>
          <a
            href="#teknopedia"
            @click="closeMobileMenu"
            class="block px-6 py-3 text-primary font-medium hover:bg-gray-50 hover:text-accent transition-all duration-200 border-l-4 border-transparent hover:border-accent"
          >
            Teknopedia
          </a>
        </li>
        <li>
          <a
            href="#kegiatan"
            @click="closeMobileMenu"
            class="block px-6 py-3 text-primary font-medium hover:bg-gray-50 hover:text-accent transition-all duration-200 border-l-4 border-transparent hover:border-accent"
          >
            Kegiatan
          </a>
        </li>
        <li>
          <a
            href="#tugas"
            @click="closeMobileMenu"
            class="block px-6 py-3 text-primary font-medium hover:bg-gray-50 hover:text-accent transition-all duration-200 border-l-4 border-transparent hover:border-accent"
          >
            Tugas
          </a>
        </li>
        <li>
          <a
            href="#faq"
            @click="closeMobileMenu"
            class="block px-6 py-3 text-primary font-medium hover:bg-gray-50 hover:text-accent transition-all duration-200 border-l-4 border-transparent hover:border-accent"
          >
            FAQ
          </a>
        </li>
      </ul>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";

// State untuk mobile menu
const isMobileMenuOpen = ref(false);

// State untuk navbar visibility
const isNavbarVisible = ref(true);
const lastScrollY = ref(0);
const hideTimeout = ref(null);
const isAtTop = ref(true);
const isAtBottom = ref(false);

// Function untuk toggle mobile menu
const toggleMobileMenu = () => {
  console.log("Toggle clicked, current state:", isMobileMenuOpen.value);
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
  console.log("New state:", isMobileMenuOpen.value);
};

// Function untuk menutup mobile menu
const closeMobileMenu = () => {
  console.log("Closing mobile menu");
  isMobileMenuOpen.value = false;
};

// Function untuk handle scroll
const handleScroll = () => {
  const currentScrollY = window.scrollY;
  const windowHeight = window.innerHeight;
  const documentHeight = document.documentElement.scrollHeight;

  // Check if at top
  isAtTop.value = currentScrollY <= 50;

  // Check if at bottom
  isAtBottom.value = currentScrollY + windowHeight >= documentHeight - 50;

  // Always show navbar at top
  if (isAtTop.value) {
    showNavbar();
    clearHideTimeout();
    return;
  }

  // Show navbar when scrolling up
  if (currentScrollY < lastScrollY.value) {
    showNavbar();
    setHideTimeout();
  }
  // Hide navbar when scrolling down (except at bottom)
  else if (currentScrollY > lastScrollY.value && !isAtBottom.value) {
    hideNavbar();
    clearHideTimeout();
  }

  lastScrollY.value = currentScrollY;
};

// Function untuk handle mouse movement (hanya di bagian bawah)
const handleMouseMove = (event) => {
  if (isAtBottom.value && event.clientY <= 100) {
    // Cursor di 100px teratas
    showNavbar();
    setHideTimeout();
  }
};

// Function untuk show navbar
const showNavbar = () => {
  isNavbarVisible.value = true;
};

// Function untuk hide navbar
const hideNavbar = () => {
  if (!isAtTop.value && !isMobileMenuOpen.value) {
    isNavbarVisible.value = false;
  }
};

// Function untuk set timeout hide
const setHideTimeout = () => {
  clearHideTimeout();
  if (!isAtTop.value) {
    hideTimeout.value = setTimeout(() => {
      if (!isMobileMenuOpen.value) {
        hideNavbar();
      }
    }, 3000); // Hide after 3 seconds
  }
};

// Function untuk clear timeout
const clearHideTimeout = () => {
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value);
    hideTimeout.value = null;
  }
};

// Function untuk handle mouse enter navbar
const handleMouseEnter = () => {
  clearHideTimeout();
};

// Function untuk handle mouse leave navbar
const handleMouseLeave = () => {
  if (!isAtTop.value && !isMobileMenuOpen.value) {
    setHideTimeout();
  }
};

// Handle resize - tutup menu ketika layar berubah ke desktop
const handleResize = () => {
  if (window.innerWidth >= 768 && isMobileMenuOpen.value) {
    closeMobileMenu();
  }
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
  window.addEventListener("scroll", handleScroll);
  window.addEventListener("mousemove", handleMouseMove);

  // Initial scroll check
  handleScroll();
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("mousemove", handleMouseMove);
  clearHideTimeout();
});
</script>
